/**
 * LeadHeader — the lead page's identity header, rendered ONCE (CRM UI
 * streamline D-6, option A).
 *
 * ContactDetail renders its header twice today (a phone branch and a desktop
 * branch behind `useIsPhone()`), with nine controls competing on one row, the
 * flag pills written out in both trees, and the three `role="alert"` error
 * lines duplicated across them. This is the single responsive replacement:
 *
 *   row 1  PageHeader — name + one chip row (SMS opted out · rating · Archived
 *          · Snoozed · CRIO subject ids) on the left; Call · Study info ·
 *          Snooze · ⋯ on the right, joined by stage + owner at ≥sm
 *   row 2  phone (+ ` · study` at <sm) · email (≥sm) · lead-since (≥sm), with
 *          the stage select at its right edge at <sm
 *   row 3  ≥sm only — the considering study + its live prescreen status
 *   row 4  "Came in for …" — the most recent landing page (≥sm)
 *   errors arch → snooze → options, as kit Alerts, rendered HERE and only here
 *
 * At <sm that collapses to exactly today's two-row phone bar — [back · name ·
 * Call · ⋯] over [phone · study — stage] — so the conversation card below
 * keeps every pixel it has now. The thread is sacred.
 *
 * Study info and Snooze sit inline at ≥sm, next to Call (owner ask
 * 2026-08-06 — restored from the classic desktop row; a hidden action was
 * getting missed). Draft options, Archive, Push to CTMS, Edit stay one click
 * deeper in `LeadActionsMenu`, which IS the existing phone bottom sheet at
 * ≤640px and an anchored popover above it — Study info and Snooze keep their
 * copies THERE too, since <sm has no room for them inline (isPhone-gated, so
 * desktop's popover does not carry a redundant copy). One implementation,
 * one mount, per width.
 *
 * DENSITY: this card sits directly above a `calc(100vh-240px)` conversation
 * card on laptops, so every saved pixel is a pixel of chat. p-3/sm:p-4 (today's
 * desktop card is p-4/sm:p-5), pills folded into one wrapping row, and the
 * secondary lines gated to ≥sm.
 *
 * SINGLE-MOUNT RULE: `useIsPhone()` is called exactly once, here, and is used
 * ONLY to decide which of two slots hosts a *stateful* control —
 * LifecycleSelect (header actions at ≥sm XOR row 2 at <sm), OwnerSelect
 * (header actions at ≥sm XOR menu row at <sm) and PrimaryStudySelect (row 3 at
 * ≥sm XOR menu row at <sm). Everything else is one instance at every width,
 * tiered with CSS classes alone. PushToCtmsButton mounts once, always inside
 * the menu.
 *
 * The parent (ContactDetail) still owns all state and every handler; this
 * component is presentation + the ⋯ open/close.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, BookOpen, CalendarDays, ChevronLeft, History, Mail, MoreHorizontal, Pencil, Phone } from 'lucide-react';
import { ContactDetailResponse, ScheduleReadiness, contactName, studyInternalLabel } from '../../types';
import { attemptStatusMeta, formatPhone, prettySlug, useIsPhone } from '../../lib';
import PageHeader from '../PageHeader';
import CallButton from '../CallButton';
import LifecycleSelect from '../LifecycleSelect';
import OwnerSelect from '../OwnerSelect';
import PrimaryStudySelect from '../PrimaryStudySelect';
import SubjectIdChips from '../SubjectIdChips';
import SnoozeButton, { isActiveSnooze, snoozeLabel } from '../SnoozeButton';
import { Alert, iconBtn } from '../ui';
import LeadActionsMenu from './LeadActionsMenu';

export interface LeadHeaderProps {
  detail: ContactDetailResponse;
  /** The CRM page this lead was opened from. Kept for parity with the page's
   *  back target; navigation itself runs through `onBack` (the parent owns the
   *  router), so this header never routes on its own. */
  backTo: string;
  backLabel: string;
  scheduleReadiness: ScheduleReadiness | null;
  primaryStudyId: string | null;
  // Errors — rendered ONCE, here (the three duplicated role="alert" lines).
  archError: string | null;
  snoozeError: string | null;
  optionsError: string | null;
  // Action state + handlers, all owned by ContactDetail.
  draftingOptions: boolean;
  onDraftOptions: () => void;
  archBusy: boolean;
  onToggleArchived: () => void;
  snoozeBusy: boolean;
  onSnooze: (untilIso: string | null) => void;
  onEdit: () => void;
  onOpenStudyRef: () => void;
  onBack: () => void;
  onLifecycleChanged: (next: string, studyId?: string) => void;
  onOwnerChanged: (next: string | null) => void;
  onPrimaryStudyChanged: (next: { id: string; slug: string; title: string } | null) => void;
}

export default function LeadHeader({
  detail,
  backLabel,
  scheduleReadiness,
  primaryStudyId,
  archError,
  snoozeError,
  optionsError,
  draftingOptions,
  onDraftOptions,
  archBusy,
  onToggleArchived,
  snoozeBusy,
  onSnooze,
  onEdit,
  onOpenStudyRef,
  onBack,
  onLifecycleChanged,
  onOwnerChanged,
  onPrimaryStudyChanged,
}: LeadHeaderProps) {
  const c = detail.contact;
  const name = contactName(c);

  // The ONE breakpoint read (see the single-mount rule above).
  const isPhone = useIsPhone();

  const [menuOpen, setMenuOpen] = useState(false);
  // Trigger + menu share this wrapper, so one containment test covers both:
  // a click on the ⋯ button is "inside" and therefore toggles rather than
  // closing-then-reopening. Anything PushToCtmsButton renders is inside too,
  // which is exactly why its dialog does not dismiss the menu.
  const menuWrapRef = useRef<HTMLDivElement>(null);

  // The lead page is NOT remounted on :id changes — drop an open menu so it
  // never carries over onto the next lead.
  useEffect(() => { setMenuOpen(false); }, [c.id]);

  // Escape on the BUBBLE phase: the capture phase belongs to the kit's
  // ConfirmDialog, which must win when it is stacked above this menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Outside-click for the anchored popover only; the phone sheet closes on its
  // own backdrop (which lives inside the wrapper, so it never double-fires).
  useEffect(() => {
    if (!menuOpen || isPhone) return;
    const onDown = (e: MouseEvent) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen, isPhone]);

  // Live prescreen status for the CURRENT study only (e.g. "Prescreening"),
  // resolved from the per-study eligibility roll-up.
  const primaryStudyStatus = (() => {
    const pid = detail.primary_study?.id;
    if (!pid) return null;
    const row = (detail.study_eligibility || []).find((se) => se.study_id === pid);
    return row ? attemptStatusMeta(row.attempt_status) : null;
  })();

  // "Came in for" — ONLY the most recent landing-page submission (the latest
  // form they completed to re-enter the system), not the full history. Screener
  // submissions arrive most-recent-first (buildScreener sorts by first_seen_at
  // DESC), so the first one with a resolvable study is the current entry reason.
  const cameInFor = useMemo(() => {
    const sourceByLeadId = new Map<string, string | null>();
    for (const l of detail.leads || []) sourceByLeadId.set(l.lead_id, l.lead_source ?? null);
    for (const s of detail.screener?.submissions || []) {
      const label = s.study_internal_title || s.study_title || prettySlug(s.study_slug);
      if (!label || label === '—') continue;
      const src = s.lead_id ? sourceByLeadId.get(s.lead_id) || null : null;
      return { label, source: src ? prettySlug(src) : null };
    }
    return null as { label: string; source: string | null } | null;
  }, [detail]);

  // Header ratings chip (Expansion Phase G) — desktop-only today, now part of
  // the one chip row at every width.
  const rating = detail.rating_summary;
  const ratingChip =
    rating != null && rating.count >= 1 && rating.average !== null
      ? { average: rating.average, count: rating.count }
      : null;
  const snoozed = isActiveSnooze(c.snoozed_until);
  const anyError = archError || snoozeError || optionsError;

  return (
    <div className="min-w-0">
      {/* Back, ≥sm: the slim link row above the card (today's desktop control). */}
      <button
        type="button"
        onClick={onBack}
        className="mb-2 hidden items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 sm:inline-flex"
      >
        <ChevronLeft className="h-4 w-4" /> {backLabel}
      </button>

      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
        <div className="flex items-start gap-1.5">
          {/* Back, <sm: an inline chevron at the row start (today's phone bar).
              A plain button is stateless, so CSS tiering is safe. */}
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            title={backLabel}
            className="-ml-1.5 inline-flex h-11 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:hidden"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* `group`: hovering anywhere in this identity block (name, phone,
              email, lead-since) reveals the edit pencil beside the name —
              a second path to EditContactModal alongside the ⋯ menu's row
              (owner ask 2026-08-06). Hover-only, so `sm:` — phones keep the
              menu as their one path. */}
          <div className="group min-w-0 flex-1">
            <PageHeader
              title={
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="min-w-0 truncate">{name}</span>
                  <button
                    type="button"
                    onClick={onEdit}
                    aria-label="Edit contact details"
                    title="Edit name, phone and email"
                    className="hidden h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 opacity-0 transition-opacity duration-150 hover:bg-gray-100 hover:text-primary-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-primary-400 sm:inline-flex"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {/* One chip row for every flag, at every width. `leading-5`
                      keeps the pills off the h1's 24px line box; `empty:hidden`
                      keeps the gap from showing when a lead has no flags. */}
                  <span className="flex flex-wrap items-center gap-1.5 font-normal leading-5 empty:hidden">
                    {c.sms_opt_out && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        <Ban className="h-3 w-3" /> SMS opted out
                      </span>
                    )}
                    {ratingChip && (
                      <span
                        title={`Average call rating across ${ratingChip.count} rating${ratingChip.count === 1 ? '' : 's'} — see the Profile tab`}
                        className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      >
                        ★{ratingChip.average.toFixed(1)} · {ratingChip.count}
                      </span>
                    )}
                    {c.archived_at && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Archived
                      </span>
                    )}
                    {snoozed && (
                      <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        Snoozed until {snoozeLabel(c.snoozed_until as string)}
                      </span>
                    )}
                    {/* CRIO subject ID(s). Keyed by contact because the lead
                        page is NOT remounted on :id changes — without it an
                        in-flight probe would paint the previous lead's subject
                        id onto this one. Renders nothing when CTMS is off. */}
                    <SubjectIdChips key={c.id} contactId={c.id} />
                  </span>
                </span>
              }
              actions={
                // flex-shrink-0 is load-bearing: PageHeader's action slot would
                // otherwise shrink at 375px and wrap Call onto its own line,
                // costing a whole row. The title beside it is `min-w-0`, so it
                // absorbs the shrink and truncates instead — which is what
                // today's phone bar does.
                <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
                  <CallButton to={c.phone_e164} contactId={c.id} name={name} />
                  {/* Study info + Snooze: header at ≥sm, next to Call (owner
                      ask 2026-08-06 — restored from the classic desktop row).
                      The phone sheet keeps its own copies for <sm, where
                      there is no room for either inline. */}
                  {!isPhone && (
                    <button
                      type="button"
                      onClick={onOpenStudyRef}
                      aria-label="Open the study reference"
                      title="Study reference: summary, recruiter script, stipend"
                      className={iconBtn}
                    >
                      <BookOpen className="h-4 w-4" />
                    </button>
                  )}
                  {!isPhone && (
                    <SnoozeButton
                      snoozedUntil={c.snoozed_until}
                      busy={snoozeBusy}
                      onSnooze={onSnooze}
                      label={name}
                      iconClassName="h-4 w-4"
                      buttonClassName={iconBtn}
                    />
                  )}
                  {/* Stage: header at ≥sm, row 2's right edge at <sm (today's
                      phone bar shape) — never both. */}
                  {!isPhone && (
                    <LifecycleSelect
                      contactId={c.id}
                      value={c.lifecycle_status}
                      scheduleReadiness={scheduleReadiness}
                      primaryStudyId={primaryStudyId}
                      onChanged={onLifecycleChanged}
                    />
                  )}
                  {/* Owner: header at ≥sm, menu row at <sm — never both. */}
                  {!isPhone && (
                    <OwnerSelect contactId={c.id} value={c.owner_user_id} onChanged={onOwnerChanged} />
                  )}
                  <div className="relative" ref={menuWrapRef}>
                    <button
                      type="button"
                      onClick={() => setMenuOpen((o) => !o)}
                      aria-label="More lead actions"
                      title="More lead actions"
                      aria-haspopup={isPhone ? 'dialog' : 'menu'}
                      aria-expanded={menuOpen}
                      className={iconBtn}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    {/* Mounted at all times (hidden when closed) so
                        PushToCtmsButton keeps its one probe per lead. */}
                    <LeadActionsMenu
                      open={menuOpen}
                      onClose={() => setMenuOpen(false)}
                      isPhone={isPhone}
                      contact={c}
                      primaryStudy={detail.primary_study ?? null}
                      draftingOptions={draftingOptions}
                      onDraftOptions={onDraftOptions}
                      archBusy={archBusy}
                      onToggleArchived={onToggleArchived}
                      snoozeBusy={snoozeBusy}
                      onSnooze={onSnooze}
                      onEdit={onEdit}
                      onOpenStudyRef={onOpenStudyRef}
                      onOwnerChanged={onOwnerChanged}
                      onPrimaryStudyChanged={onPrimaryStudyChanged}
                    />
                  </div>
                </div>
              }
            />

            {/* Row 2 — contact reach, and at <sm the stage select at its right
                edge: that reproduces today's phone bar ("phone · study" left,
                LifecycleSelect right) so the header costs the conversation no
                extra rows on a 375px screen. Email and lead-since are
                secondary and stay off that screen entirely. */}
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 sm:text-sm sm:text-gray-600">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <Phone className="hidden h-3.5 w-3.5 flex-shrink-0 text-gray-400 sm:inline" />
                  <span className="truncate">
                    {formatPhone(c.phone_e164)}
                    {/* <sm only: the considering study rides the phone line
                        rather than earning a row of its own (row 3 is ≥sm).
                        Nothing at all when unset — today's exact pattern. */}
                    <span className="sm:hidden">
                      {detail.primary_study
                        ? ` · ${studyInternalLabel(detail.primary_study)}`
                        : ''}
                    </span>
                  </span>
                </span>
                <span className="hidden min-w-0 items-center gap-1.5 sm:inline-flex">
                  <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{c.email || '—'}</span>
                </span>
                <span className="hidden items-center gap-1.5 sm:inline-flex">
                  <CalendarDays className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" /> Lead since{' '}
                  {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
              {isPhone && (
                <LifecycleSelect
                  contactId={c.id}
                  value={c.lifecycle_status}
                  scheduleReadiness={scheduleReadiness}
                  primaryStudyId={primaryStudyId}
                  onChanged={onLifecycleChanged}
                />
              )}
            </div>

            {/* Row 3 — ≥sm ONLY: the editable primary-study selector plus that
                study's live prescreen status. Editable at EVERY status, closed
                leads included (owner 2026-08-07): a lead who didn't qualify for
                one study is exactly the lead you want to move onto the next
                study's list, and the old "No study assigned" pill forced a trip
                through the status dropdown first. The status badge already says
                the lead is closed, so nothing is lost by dropping the pill.
                Phones read the study off row 2 and change it in the ⋯ menu, so
                this row costs them nothing.

                Gated by BOTH `!isPhone` and `hidden sm:flex`: the JS guard is
                what keeps PrimaryStudySelect to a single mount (a
                `hidden`-only row would still mount a second one behind the
                menu's), and the CSS guard keeps the row from flashing during a
                resize before matchMedia reports in. */}
            {!isPhone && (
            <div className="mt-1 hidden flex-wrap items-center gap-x-2 gap-y-1 sm:flex">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Considering</span>
              <PrimaryStudySelect
                contactId={c.id}
                studyId={detail.primary_study?.id ?? null}
                studyTitle={detail.primary_study ? studyInternalLabel(detail.primary_study) : null}
                lifecycleStatus={c.lifecycle_status}
                onChanged={onPrimaryStudyChanged}
                compact
              />
              {primaryStudyStatus && (
                <span
                  title={`Prescreen status for ${
                    detail.primary_study ? studyInternalLabel(detail.primary_study) : 'this study'
                  }`}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${primaryStudyStatus.badge}`}
                >
                  {primaryStudyStatus.label}
                </span>
              )}
            </div>
            )}

            {/* Row 4 — most recent entry reason: the latest landing page they
                completed to re-enter the system (history only, NOT the
                considered study). */}
            {cameInFor && (
              <p
                className="mt-1 hidden max-w-full items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 sm:inline-flex"
                title="The most recent landing page this lead completed"
              >
                <History className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  Came in for {cameInFor.label}
                  {cameInFor.source ? ` (via ${cameInFor.source})` : ''}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Errors — one place, one recipe, in the order the actions fire. */}
        {anyError && (
          <div className="mt-2 space-y-1.5">
            {archError && (
              <Alert tone="error" size="sm" icon={false}>
                {archError}
              </Alert>
            )}
            {snoozeError && (
              <Alert tone="error" size="sm" icon={false}>
                {snoozeError}
              </Alert>
            )}
            {optionsError && (
              <Alert tone="error" size="sm" icon={false}>
                {optionsError}
              </Alert>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
